import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows live debounced AJAX search without full page reload.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    // Listen to popstate (browser back/forward navigation)
    window.addEventListener('popstate', this.#handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#handlePopState);
  }

  /**
   * Handles the input event on the search input with debouncing.
   * Triggers live AJAX search as the user types without refreshing the page.
   */
  handleInput = debounce(() => {
    const value = this.refs.searchPageInput.value.trim().toLowerCase();
    this.#performSearch(value);
  }, 100);

  /**
   * Handles the keydown event on the search input.
   * Prevents Enter key from submitting/reloading the page.
   * Clears input on Escape.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      return;
    }

    if (event.key === 'Escape') {
      const input = this.refs.searchPageInput;
      if (input) {
        input.value = '';
        this.#performSearch('');
      }
    }
  };

  /**
   * Performs an asynchronous search fetch and updates the DOM without reloading the page.
   *
   * @param {string} query - The search query.
   */
  async #performSearch(query) {
    // Abort any ongoing search request
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();

    const url = new URL(window.location.href);
    url.pathname = '/search';
    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }
    url.searchParams.delete('page');
    // Drop the previously chosen tab: it belongs to the old query, and keeping it
    // pins the view to a tab that may have no results for the new one. Without it
    // the section falls back to auto-selecting the first tab that has results.
    url.searchParams.delete('tab');

    // Update URL bar without triggering page refresh
    window.history.replaceState({}, '', url.toString());

    try {
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) return;

      const htmlText = await response.text();
      const newDoc = new DOMParser().parseFromString(htmlText, 'text/html');

      // 1. Update Subtitle / Status message above input
      const currentSubtitle = document.getElementById('search-status-message');
      const newSubtitle = newDoc.getElementById('search-status-message');

      if (currentSubtitle && newSubtitle) {
        currentSubtitle.innerHTML = newSubtitle.innerHTML;
        currentSubtitle.style.display = newSubtitle.style.display;
      } else if (currentSubtitle) {
        currentSubtitle.style.display = query ? '' : 'none';
      }

      // 2. Update both result sections.
      //    The results list and the empty state are separate sections, and each
      //    renders nothing in the state the other owns — so swapping only the
      //    populated one would leave the previous state on screen. Replace both
      //    section wrappers and whichever is empty for this query collapses.
      for (const selector of ['.search-results-section', '.search-empty-state-section']) {
        const current = document.querySelector(selector);
        const next = newDoc.querySelector(selector);

        if (current && next) {
          current.innerHTML = next.innerHTML;
        }
      }

      // 3. Dispatch event so tab switching script and collection fetches re-initialize
      document.dispatchEvent(new CustomEvent('search:results-updated', { detail: { query } }));
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching live search results:', err);
      }
    }
  }

  /**
   * Handles browser back/forward buttons
   */
  #handlePopState = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q') ?? '';

    if (this.refs.searchPageInput) {
      this.refs.searchPageInput.value = query;
    }
    this.#performSearch(query.toLowerCase());
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
